import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Row, Col, Card, Statistic, Table, Tag, Typography, message } from 'antd';
import PujaSpinner from '../../components/PujaSpinner';
import useColumnasResponsive from '../../components/tabla/useColumnasResponsive';
import { fecha } from '../../utils/format';
import TagCategoria from '../../components/TagCategoria';
import {
  UnorderedListOutlined,
  CalendarOutlined,
  TeamOutlined,
  DollarOutlined,
  BookOutlined,
} from '@ant-design/icons';
import { dashboardService } from '../../services/api';
import { useAuth } from '../../context/AuthContext';

const { Title } = Typography;

const Dashboard = () => {
  const { soloLectura, tieneRecurso } = useAuth();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    totalAnimales: 0,
    eventosActivos: 0,
    totalClientes: 0,
    ofertasHoy: 0,
  });
  const [eventosActivos, setEventosActivos] = useState([]);
  const [ultimosAnimales, setUltimosAnimales] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      // 1 sola request al endpoint BFF (antes eran 2 serializadas).
      const { data } = await dashboardService.getStats();
      setEventosActivos(data.eventos_activos_top || []);
      setUltimosAnimales(data.ultimos_animales || []);
      setStats({
        totalAnimales: data.counts?.total_animales ?? 0,
        eventosActivos: data.counts?.eventos_activos ?? 0,
        totalClientes: data.counts?.total_clientes ?? 0,
        ofertasHoy: data.counts?.ofertas_hoy ?? 0,
      });
    } catch (error) {
      message.error(
        error.response?.data?.error || 'Error al cargar los datos del dashboard'
      );
    } finally {
      setLoading(false);
    }
  };

  // Columnas para tabla de eventos
  const eventosColumns = [
    {
      title: 'Evento',
      dataIndex: 'nombre_evento',
      key: 'nombre_evento',
    },
    {
      title: 'Fecha Inicio',
      dataIndex: 'fecha_inicio',
      key: 'fecha_inicio',
      // El backend manda un DATE en ISO ('2026-08-15') y sin `render` se
      // mostraba crudo. Va por `fecha()` y no por `dayjs(v).format(...)` suelto
      // por el vacio: `dayjs(undefined)` devuelve HOY sin avisar. La celda
      // apilada de mobile reusa este mismo render, asi que las dos vistas se
      // arreglan con una sola linea.
      render: (v) => fecha(v),
    },
    {
      title: 'Fecha Fin',
      dataIndex: 'fecha_fin',
      key: 'fecha_fin',
      render: (v) => fecha(v),
    },
    {
      title: 'Estado',
      key: 'estado',
      render: (_, record) => (
        <Tag color={record.estado_evento === 1 ? 'green' : 'red'}>
          {record.estado_evento === 1 ? 'Activo' : 'Inactivo'}
        </Tag>
      ),
    },
    {
      title: 'Acción',
      key: 'action',
      render: (_, record) => (
        <a onClick={() => navigate(`/eventos/${record.id_evento}`)}>Ver</a>
      ),
    },
  ];

  // Columnas para tabla de animales
  const animalesColumns = [
    {
      title: 'Corral',
      dataIndex: 'corral',
      key: 'corral',
      width: 80,
    },
    {
      title: 'Nombre',
      dataIndex: 'nom_animal',
      key: 'nom_animal',
    },
    {
      title: 'RP',
      dataIndex: 'rp_animal',
      key: 'rp_animal',
    },
    {
      title: 'Categoría',
      key: 'categoria',
      // El endpoint /api/dashboard/stats aplana `nombre_categoria`; se soporta
      // tambien el shape anidado por si quedaron referencias. COMO se ve lo
      // decide TagCategoria, para que no vuelva a verse distinta que en el
      // listado de animales.
      render: (_, record) => (
        <TagCategoria valor={record.nombre_categoria || record.categoria?.nom_categoria} />
      ),
    },
    {
      title: 'Acción',
      key: 'action',
      render: (_, record) => (
        <a onClick={() => navigate(`/animales/${record.id_animal}`)}>Ver</a>
      ),
    },
  ];

  /* VISTA MOBILE DE LAS TABLAS: 2 columnas, sin ocultar datos y sin obligar a
   * scrollear a la derecha. El dato principal va arriba y el resto se apila
   * abajo. El por que de este camino —y por que las columnas se referencian
   * por `key` en vez de repetir el `render`— esta en useColumnasResponsive.
   *
   * Los hooks van ACA, antes del `if (loading)`: despues de un early return
   * se llamarian condicionalmente y React rompe. */
  const { columnas: columnasEventos, propsTabla: propsEventos } = useColumnasResponsive({
    desktop: eventosColumns,
    // En desktop se conserva el scroll horizontal que puso CR-2. El helper lo
    // anula en mobile: ver el comentario sobre `max-content` alla.
    scroll: { x: 'max-content' },
    mobile: {
      title: 'Evento',
      principal: 'nombre_evento',
      secundarios: [
        // Con DOS fechas en la misma linea las etiquetas dejan de ser opcionales:
        // "15/08/2026 · 20/08/2026" no dice cual es cual.
        { key: 'fecha_inicio', etiqueta: 'F.I' },
        { key: 'fecha_fin', etiqueta: 'F.F' },
        // Sin etiqueta a proposito: es un <Tag> de color que ya dice
        // "Activo"/"Inactivo". La etiqueta va donde el dato es ambiguo.
        { key: 'estado' },
      ],
      conservar: ['action'],
    },
  });

  const { columnas: columnasAnimales, propsTabla: propsAnimales } = useColumnasResponsive({
    desktop: animalesColumns,
    scroll: { x: 'max-content' },
    mobile: {
      title: 'Datos',
      principal: 'nom_animal',
      // Estos tres SI llevan etiqueta: "12 · 4501 · Toro" no dice cual es el
      // corral y cual el RP. Son dos numeros pelados.
      secundarios: [
        { key: 'corral', etiqueta: 'Corral' },
        { key: 'rp_animal', etiqueta: 'RP' },
        // Sin etiqueta, igual que en AnimalesList: desde que la categoria es un
        // chip de color se explica sola. Llevaba "Cat:" de cuando era texto
        // pelado, y esa era justamente la diferencia entre las dos tablas.
        { key: 'categoria' },
      ],
      conservar: ['action'],
    },
  });

  if (loading) {
    return (
      <PujaSpinner size="lg" />
    );
  }

  return (
    <div>
      <Title level={2}>Dashboard</Title>
      
      {/* Estadísticas - 5 Cards */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={12} sm={8} lg={4}>
          <Card className="stat-card" hoverable onClick={() => navigate('/animales')}>
            <Statistic
              title="Total Animales"
              value={stats.totalAnimales}
              prefix={<UnorderedListOutlined />}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
        <Col xs={12} sm={8} lg={5}>
          <Card className="stat-card" hoverable onClick={() => navigate('/eventos')}>
            <Statistic
              title="Eventos Activos"
              value={stats.eventosActivos}
              prefix={<CalendarOutlined />}
              valueStyle={{ color: '#52c41a' }}
            />
          </Card>
        </Col>
        {/* La card de Clientes solo para quien OPERA su cabania.
            Una cabania invitada no tiene clientes propios: los compradores son
            de la cabania organizadora del remate. Mostrarle el numero —y peor,
            un card `hoverable` que la lleva a una pantalla que el menu ya le
            oculto— es contarle algo ajeno. Mismo `soloLectura` que gatea
            Clientes y Ventas en el menu, para que las dos superficies no puedan
            discrepar. */}
        {!soloLectura && (
          <Col xs={12} sm={8} lg={5}>
            <Card className="stat-card" hoverable onClick={() => navigate('/clientes')}>
              <Statistic
                title="Clientes"
                value={stats.totalClientes}
                prefix={<TeamOutlined />}
                valueStyle={{ color: '#722ed1' }}
              />
            </Card>
          </Col>
        )}
        <Col xs={12} sm={8} lg={5}>
          {/* SIN `hoverable`, a diferencia de los otros cuatro: esos tienen
              `onClick` y llevan a algun lado. `hoverable` pone
              `cursor: pointer` y levanta la sombra, o sea que el card anuncia
              "tocame" y no hace nada. Misma afordancia que miente que el
              `sorter` que se saco de AnimalesList. */}
          <Card className="stat-card">
            <Statistic
              title="Ofertas Hoy"
              value={stats.ofertasHoy}
              prefix={<DollarOutlined />}
              valueStyle={{ color: '#faad14' }}
            />
          </Card>
        </Col>
        {/* La card de Micrositio va con `config_identidad`, el MISMO recurso que
            gatea el item del menu. Si las dos superficies no usan el mismo
            criterio, tarde o temprano discrepan.
            Y tenia dos formas de mentir, las dos reales:
            - SIN slug caia en `navigate('/herramientas/micrositio')`, la
              pantalla que el menu le escondio a proposito. La ruta no tiene
              guarda por recurso, asi que entraba, configuraba y se comia el 403
              al guardar.
            - CON slug era peor todavia para una cabania invitada: desde que ve
              el remate ajeno, `eventosActivos` incluye el de la organizadora,
              y la card llamada "Micrositio" le abria el micrositio de OTRO.
            El micrositio es la herramienta de quien opera su cabania. La
            invitada no tiene uno, y una card que dice que si es una afordancia
            que miente — lo mismo que este archivo ya dice tres cards mas
            abajo sobre `hoverable`. */}
        {tieneRecurso('config_identidad') && (
        <Col xs={12} sm={8} lg={5}>
          <Card
            className="stat-card"
            hoverable
            onClick={() => {
              const slug = eventosActivos.find(e => e.slug)?.slug;
              if (slug) {
                window.open(`/remate/${slug}`, '_blank');
              } else {
                navigate('/herramientas/micrositio');
              }
            }}
            style={{ borderColor: '#13c2c2' }}
          >
            <Statistic
              title="Micrositio"
              value="Ver"
              prefix={<BookOutlined />}
              valueStyle={{ color: '#13c2c2', fontSize: '20px' }}
            />
          </Card>
        </Col>
        )}
      </Row>

      {/* Tablas */}
      <Row gutter={[16, 16]}>
        <Col xs={24} lg={12}>
          <Card 
            title="Eventos Activos" 
            extra={<a onClick={() => navigate('/eventos')}>Ver todos</a>}
          >
            <Table
              {...propsEventos}
              dataSource={eventosActivos}
              columns={columnasEventos}
              rowKey="id_evento"
              pagination={false}
              size="small"
            />
          </Card>
        </Col>
        <Col xs={24} lg={12}>
          <Card 
            title="Últimos Animales" 
            extra={<a onClick={() => navigate('/animales')}>Ver todos</a>}
          >
            <Table
              {...propsAnimales}
              dataSource={ultimosAnimales}
              columns={columnasAnimales}
              rowKey="id_animal"
              pagination={false}
              size="small"
            />
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default Dashboard;